/**
 * The board's queue predicates — *Needs you* and *Awaiting approval* — in one
 * place, so the web app and the API answer the same question the same way.
 *
 * They lived in the web app's selectors, which left a script reading the API
 * to re-implement them, and two copies of a rule this subtle drift. The web
 * app re-exports them from `selectors.ts`; the API serves them on
 * `GET /projects/:id/tasks?queue=`.
 */

import { HEARTBEAT_STALE_MS, isHeartbeatStale, isReviewable, isWaitingOnDesign, type Task } from './domain.ts';
import { HttpError } from './http.ts';

export const LANDED = new Set(['merged', 'deployed']);

/**
 * Whether the viewer is an admin, for the purpose of deciding what a task
 * needs from them.
 *
 * A plain boolean is enough for any screen scoped to one project — every task
 * on it shares the same project, so the same yes/no applies to all of them.
 * The "All projects" board breaks that: two tasks on the same screen can
 * belong to projects where the viewer's role differs. A function lets each
 * task answer for its own project; a screen with only one project to worry
 * about can keep passing a plain boolean, unchanged.
 */
export type IsAdmin = boolean | ((task: Task) => boolean);

export function adminFor(isAdmin: IsAdmin, task: Task): boolean {
  return typeof isAdmin === 'function' ? isAdmin(task) : isAdmin;
}

/**
 * Is this task asking something of *you*?
 *
 * Three quite different things, and the reason `agent_state` exists as a field
 * separate from the ladder is that only the first of them was previously
 * knowable:
 *
 *   1. the routine said it is parked and needs a human;
 *   2. there is something to look at, nobody has vouched for it, and nobody is
 *      still working on it;
 *   3. somebody vouched for it and you are the admin who can approve.
 *
 * The third clause of (2) is the one that keeps this queue worth reading — see
 * the comment on the reviewable check below.
 */
export function needsYou(task: Task, isAdmin: IsAdmin, now: number): boolean {
  // No callback token was ever minted for this one, so nothing can move
  // `agent_state` off its default — there is no report coming that would
  // make this a genuine question for anybody.
  if (task.source === 'background') return false;
  if (task.status === 'error') return false; // Failed is its own queue
  if (LANDED.has(task.status)) return false;
  // Its pull request landed, whatever the ladder still says — a task can lag
  // its merge (another pull request still recorded open, a routine's stale
  // `blocked`), and a merged change is not asking anybody anything. The same
  // exclusion *Awaiting approval* makes.
  if (task.pr_state === 'merged') return false;
  // Withdrawn, not merely stalled — the pull request closed without landing,
  // and nothing is waiting on anyone any more.
  if (task.archived_at) return false;
  // In the trash or parked — a person put it there on purpose, which answers
  // the question this queue exists to ask.
  if (task.deleted_at) return false;
  if (task.snoozed_at) return false;

  // A routine that says it is stuck is asking, whatever the task's settings —
  // and whatever the ladder says, which is why this sits above the approved
  // check rather than below it. Approval answers "may this land?". It does not
  // answer "one check is red and I do not think it is mine, do you still want
  // it landed?", which is a different question that only turns up *after* the
  // approval. Below the check, a task in exactly that state said "waiting on
  // you" on its face and appeared in no queue at all: the one routine that did
  // the right thing and asked was the one nobody heard.
  if (task.agent_state === 'waiting_on_you') return true;

  // Blocked is the same handover said less politely, and for the same reason it
  // sits above the approved check. An approved task is exactly where it turns
  // up: the merge driver parks one here when GitHub refuses the merge, and a
  // routine parks one here when it cannot supply the reviewed head the merge
  // is gated on. Below the check, "approved" hid every one of them, and the
  // Merging queue told you there was nothing left for you to do.
  if (task.agent_state === 'blocked') return true;

  // Handed to a design pass, with a routine coming back for it. Nothing here is
  // a person's to do — the routine stopped on purpose, and the preview it left
  // is about to be built over — so an early link must not read as "review".
  if (isWaitingOnDesign(task)) return false;

  // "Come and look" is a different handover from "answer me", and it is the one
  // that can arrive before there is anything to act on. A routine that has
  // finished building and says so is telling the truth, but if the preview is
  // still deploying and the checks are red, a person opening this task can only
  // close it again. So the invitation waits for the thing it is inviting you to.
  //
  // Deferred, not dropped: if no preview turns up within the window we would
  // have given a silent routine, nobody is coming with one, and a handover with
  // nothing attached is itself something a person needs to see. The failure
  // this must not have is a state that quietly swallows finished work.
  if (task.agent_state === 'ready_for_review') {
    // Somebody already said yes. "Come and look" is the invitation that leads
    // to an approval, so once there is one it has nothing left to ask; this
    // sits inside the branch because the branch returns before the approved
    // and auto-merge checks below ever see the task.
    if (task.status === 'approved' || (task.auto_merge && !(task.merge_held_by && !task.approved_at))) return false;
    if (isReviewable(task)) return true;
    return now - Date.parse(task.agent_heartbeat_at ?? task.updated_at) > HEARTBEAT_STALE_MS;
  }

  // An import nobody picked up.
  //
  // Adopting a pull request posts an invitation and then waits, and the whole
  // arrangement rests on somebody answering it. When nobody does, the result is
  // the worst thing on this board: a row that looks covered and never says
  // anything, which is less honest than never having imported it. You asked to
  // see what was happening on that pull request, and the answer turned out to
  // be "nothing is going to tell you" — that is a fact you are owed, not one to
  // sit on.
  //
  // Both conditions matter. `claimed_at` is empty for a routine that reported
  // using an agent token it already held, which is a success, not a silence —
  // so the heartbeat is what actually distinguishes "nobody came" from "somebody
  // came in by the other door".
  //
  // Only for an import a person asked for. The sweep adopts every pull request
  // on its own, people's and local sessions' alike, and most of those nobody
  // ever claims — the task says "Nobody is on this", and filing each one here
  // as well would fill the queue with rows nobody asked to watch.
  if (
    task.source === 'import' &&
    task.created_by &&
    // Handed to a routine: Rigg dispatched it, so it is a routine's silence now.
    task.listener !== 'routine' &&
    !task.claimed_at &&
    !task.agent_heartbeat_at &&
    now - Date.parse(task.created_at) > HEARTBEAT_STALE_MS
  ) {
    return true;
  }

  if (task.status === 'approved') return false; // on its way in, nothing to do

  // Auto-merge is a standing approval given when the task was raised. Asking
  // again is asking twice, and a queue that fills with work nobody has to look
  // at is a queue people stop reading.
  //
  // Unless the merge driver is holding it: the pull request touches one of the
  // repository's protected paths, which the flag never waives, so the merge
  // is waiting on a person's approval and an admin is the person.
  if (task.merge_held_by && !task.approved_at) {
    // Not while a routine is visibly still on it — the same courtesy every
    // other task gets below.
    if (task.agent_state === 'working' && !isHeartbeatStale(task, now)) return false;
    return adminFor(isAdmin, task);
  }
  if (task.auto_merge) return false;

  if (task.status === 'signed_off') return adminFor(isAdmin, task);

  // You have replied and the ball is with the routine. Placed after the
  // approval check on purpose: approval is a different question, and answering
  // one does not answer the other. Without this the task drops straight back in
  // on the reviewable check below — asking you to look at a preview you were
  // looking at when you wrote the reply.
  if (task.agent_state === 'answered') return false;

  // A preview link is an invitation to look, not a summons.
  //
  // The protocol tells routines to publish one "as soon as anything is
  // viewable, not when you are finished", which is right — it is how you get to
  // see the wrong thing being built while it is still cheap to say so. But this
  // queue then read that early link as a request for review, so a routine
  // following the instruction correctly put itself in front of a person while
  // it was still mid-build, with checks running and its own status line saying
  // it was watching them. The two rules were fighting, and the queue lost:
  // every well-behaved routine filed a request nobody could act on.
  //
  // So the link stops meaning "review this" for as long as somebody is visibly
  // still on it. The routine asks by saying so — `waiting_on_you`, `done`, a
  // sign-off — or by stopping, at which point silence turns it stale and the
  // preview speaks for itself. Nothing is hidden, only deferred until there is
  // an answer a person could actually give.
  if (task.agent_state === 'working' && !isHeartbeatStale(task, now)) return false;

  return isReviewable(task);
}

/* ── the approval queue ─────────────────────────────────────────────────── */

/**
 * Is this task's pull request waiting on a person to approve it?
 *
 * Narrower than *Needs you*, on purpose. That queue is every way a task can be
 * asking something; this is the one question an admin can answer from the
 * list without opening anything — an open, ready pull request that nobody has
 * cleared to land. Which is why everything that already has an answer is out:
 * approved, auto-merge still standing, landed, failed, withdrawn, parked, and
 * anything a routine is visibly still building, since approving a moving
 * target is approving something nobody has seen.
 */
export function awaitingApproval(task: Task, now: number): boolean {
  if (task.source === 'background') return false;
  if (task.deleted_at || task.snoozed_at || task.archived_at) return false;
  if (LANDED.has(task.status) || task.status === 'error' || task.status === 'approved') return false;
  if (!task.pr_url || task.pr_draft) return false;
  if (task.pr_state === 'merged' || task.pr_state === 'closed') return false;
  // Cleared already — the same two doors `isMergeCandidate` reads, and spent
  // by a send-back exactly as it spends them.
  // A protected-path hold is the merge driver saying the flag does not reach
  // this pull request, so it is waiting on an approval like any other.
  if (task.auto_merge && !task.reopened_at && !task.merge_held_by) return false;
  if (task.approved_at && !(task.reopened_at && task.reopened_at > task.approved_at)) return false;
  if (task.agent_state === 'working' && !isHeartbeatStale(task, now)) return false;
  return true;
}

/**
 * Least risky first, then the longest waiting. A task with no measured risk
 * goes after every task that has one — unknown is not "low".
 */
export function byRiskThenAge(a: Task, b: Task): number {
  const ra = a.risk?.level ?? 4;
  const rb = b.risk?.level ?? 4;
  if (ra !== rb) return ra - rb;
  return Date.parse(a.created_at) - Date.parse(b.created_at);
}


/* ── served over the API ────────────────────────────────────────────────── */

export const API_QUEUES = ['needs-you', 'awaiting-approval'] as const;
export type ApiQueue = (typeof API_QUEUES)[number];

/**
 * One project's tasks, cut down to a queue, the way the board cuts them.
 *
 * In memory rather than in SQL because the predicates read a dozen columns and
 * a heartbeat clock that a query cannot express. *Awaiting approval* comes back
 * in the order the board shows it — least risky first, then longest waiting;
 * *Needs you* keeps the order it was given.
 */
export function applyQueue<T extends Task>(tasks: T[], queue: string, isAdmin: IsAdmin, now: number): T[] {
  switch (queue) {
    case 'needs-you':
      return tasks.filter((task) => needsYou(task, isAdmin, now));
    case 'awaiting-approval':
      return tasks.filter((task) => awaitingApproval(task, now)).sort(byRiskThenAge);
    default:
      throw new HttpError(400, `queue must be one of ${API_QUEUES.join(', ')}`);
  }
}

/** PostgREST's `max-rows` on Supabase — a single read never returns more. */
export const QUEUE_PAGE_SIZE = 1000;

/**
 * Every row a query would return, read a page at a time until a short page.
 *
 * A single `.limit(n)` read is silently capped at `max-rows`, so a queue cut
 * over it loses whatever is older than the newest page. `fetchPage` reads the
 * inclusive range `[from, to]` with a stable order.
 */
export async function readAllPages<T>(
  fetchPage: (from: number, to: number) => Promise<T[]>,
  pageSize = QUEUE_PAGE_SIZE,
): Promise<T[]> {
  const rows: T[] = [];
  for (let from = 0; ; from += pageSize) {
    const page = await fetchPage(from, from + pageSize - 1);
    rows.push(...page);
    if (page.length < pageSize) return rows;
  }
}
